<script>
    import { mapActions, mapMutations, mapState } from 'vuex';
    import { Dialog } from 'element-ui';
    import ReusableMethods from '../mixins/reusable_methods';
    import Vue from 'vue';
    import VueButton from '~/components/button/components/button';

    Vue.use(Dialog);

    export default {
        name: 'session-inactivity-timeout-dialog',

        components: {
            VueButton
        },

        mixins: [
            ReusableMethods
        ],

        mounted() {
            this.updateSessionExpirationTime(this.initialSessionExpirationTime);
        },

        props: {
            remainingTimeWarningSeconds: {
                type: Number,
                required: true
            },

            initialSessionExpirationTime: {
                type: Number,
                required: true
            },
        },

        computed: {
            ...mapState('sessionTimeout', [
                'dialogsVisible',
                'formattedInactivityTime',
                'forceStopWarningTimer',
                'noRefresh',
                'sessionExpirationTime',
                'sessionTimer',
                'warningTimer',
            ]),

            dialogVisible: {
                get() {
                    return this.dialogsVisible.inactivityDialog;
                },
                set(val) {
                    this.updateDialogsVisible({ inactivityDialog: val });
                }
            },

            remainingSeconds: {
                cache: false,
                get: function() {
                    return this.sessionExpirationTime - this.getCurrentTime();
                }
            },
        },

        watch: {
            forceStopWarningTimer: function(newValue, oldValue) {
                if (newValue && this.dialogVisible === true) {
                    this.updateDialogsVisible({
                        inactivityDialog: false
                    });
                }
            },

            sessionExpirationTime: function(newValue, oldValue) {
                this.startSessionTimer();
            },
        },

        methods: {
            ...mapActions('sessionTimeout', [
                'checkSession',
                'refreshSession'
            ]),

            ...mapMutations('sessionTimeout', [
                'updateDialogsVisible',
                'updateFormattedInactivityTime',
                'updateSessionExpirationTime',
                'updateSessionTimer',
                'updateNoRefresh',
                'updateWarningTimer'
            ]),

            startSessionTimer() {
                if (this.sessionTimer !== null) {
                    clearTimeout(this.sessionTimer);
                    this.updateSessionTimer(null);
                }

                if (!this.forceStopWarningTimer) {
                    const dialogStartTime = this.sessionExpirationTime - this.remainingTimeWarningSeconds;
                    const timeUntilWarningDialog = Math.max(dialogStartTime - this.getCurrentTime(), 0);

                    const timer = setTimeout(() => {

                        this.updateSessionTimer(null);
                        this.checkSession().then(data => {
                            if (data.next_expiration > this.sessionExpirationTime) {
                                this.updateSessionExpirationTime(data.next_expiration);
                            } else if (this.getCurrentTime() >= this.sessionExpirationTime) {
                                this.logout();
                            } else {
                                this.openDialog();
                            }
                        }).catch(xhr => {
                            this.logout();
                        });
                    }, timeUntilWarningDialog * 1000);

                    this.updateSessionTimer(timer);
                }
            },

            initializeDialog() {
                this.updateNoRefresh(false);
                this.stopWarningTimer();
                this.updateWarningTimer(setInterval(this.tick, 1000));
            },

            openDialog() {
                this.formatTime(this.remainingSeconds);
                this.updateDialogsVisible({ inactivityDialog: true });
            },

            closeDialog() {
                this.stopWarningTimer();

                if (!this.noRefresh) {
                    this.refreshSession().then(() => {
                        this.startSessionTimer();
                    }).catch(() => {
                        this.logout();
                    });
                }
            },

            tick() {
                if (this.remainingSeconds > 0) {
                    this.formatTime(this.remainingSeconds);
                } else {
                    this.stopWarningTimer();
                    this.checkSession().then(data => {
                        if (data.next_expiration > this.sessionExpirationTime) {
                            this.updateNoRefresh(true);
                            this.updateDialogsVisible({ inactivityDialog: false });
                            this.updateSessionExpirationTime(data.next_expiration);
                        } else {
                            this.logout();
                        }
                    })
                }
            },

            stopWarningTimer() {
                if (this.warningTimer !== null) {
                    clearInterval(this.warningTimer);
                    this.updateWarningTimer(null);
                }
            },

            userRefresh() {
                this.updateDialogsVisible({ inactivityDialog: false });
            },
        }
    };
</script>

<template>
    <el-dialog
        title="Session Timeout"
        :close-on-click-modal="false"
        :show-close="true"
        :visible.sync="dialogVisible"
        size="tiny"
        @open="initializeDialog"
        @close="closeDialog">
            <p>
                Your session is about to expire.<br>
                You will be automatically signed out in:
            </p>
            <h3 class="text-center">{{ formattedInactivityTime }}</h3>
            <p>Do you want to stay signed in?</p>

            <div slot="footer">
                <vue-button
                    type="white"
                    size="sm"
                    class="m-r-5"
                    @click="userLogout">
                    Sign Out
                </vue-button>

                <vue-button
                    type="primary"
                    size="sm"
                    @click="userRefresh">
                    Stay Signed In
                </vue-button>
            </div>
    </el-dialog>
</template>

